import type { WebSocketMessage } from '@/types';

type EventCallback = (data: any) => void;

interface QueuedMessage {
  event: string;
  data: any;
}

interface SyncEvent {
  id: number;
  eventType: string;
  payload: any;
  createdAt: string;
}

interface SyncResponse {
  events: SyncEvent[];
  lastEventId: number;
  hasMore: boolean;
}

const LAST_EVENT_ID_KEY = 'ws_last_event_id';

class WebSocketService {
  private ws: WebSocket | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 15;
  private baseReconnectDelay = 1000;
  private companyId: number | null = null;
  private messageQueue: QueuedMessage[] = [];
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManuallyDisconnected = false;
  private lastPong: number = Date.now();
  private visibilityChangeListener: (() => void) | null = null;
  
  // Sync-related properties
  private lastEventId: number = 0;
  private processedEventIds: Set<number> = new Set();
  private maxProcessedEventsCache = 1000; // Keep last 1000 event IDs to prevent duplicates
  private isSyncing = false;

  constructor() {
    // Load last event ID from storage
    this.loadLastEventId();
    // Setup visibility change listener
    this.setupVisibilityListener();
  }

  /**
   * Load last event ID from localStorage
   */
  private loadLastEventId(): void {
    try {
      const stored = localStorage.getItem(LAST_EVENT_ID_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.companyId === this.companyId) {
          this.lastEventId = parsed.lastEventId || 0;
        }
      }
    } catch {
      this.lastEventId = 0;
    }
  }

  /**
   * Save last event ID to localStorage
   */
  private saveLastEventId(): void {
    try {
      localStorage.setItem(LAST_EVENT_ID_KEY, JSON.stringify({
        companyId: this.companyId,
        lastEventId: this.lastEventId,
      }));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Update last event ID and clean up old processed IDs
   */
  private updateLastEventId(eventId: number): void {
    if (eventId > this.lastEventId) {
      this.lastEventId = eventId;
      this.saveLastEventId();
    }
    
    // Track processed event ID
    this.processedEventIds.add(eventId);
    
    // Clean up old processed event IDs to prevent memory bloat
    if (this.processedEventIds.size > this.maxProcessedEventsCache) {
      const idsArray = Array.from(this.processedEventIds).sort((a, b) => a - b);
      const toRemove = idsArray.slice(0, idsArray.length - this.maxProcessedEventsCache / 2);
      toRemove.forEach(id => this.processedEventIds.delete(id));
    }
  }

  /**
   * Check if an event has already been processed
   */
  private isEventProcessed(eventId: number): boolean {
    // Only check if we've explicitly seen this ID before
    // Don't assume events with lower IDs are duplicates since real-time and sync events may use different sequences
    return this.processedEventIds.has(eventId);
  }

  /**
   * Send acknowledgment for received event
   */
  private sendAck(eventId: number): void {
    if (eventId > 0) {
      this.send('ack', { eventId });
    }
  }

  /**
   * Setup visibility change listener to reconnect when tab becomes visible
   */
  private setupVisibilityListener(): void {
    this.visibilityChangeListener = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ [WebSocket] Tab became visible, checking connection...');
        
        // If we have a companyId but not connected, reconnect
        if (this.companyId && !this.isConnected()) {
          console.log('🔄 [WebSocket] Reconnecting after tab became visible');
          this.reconnectAttempts = 0; // Reset attempts on manual reconnect
          this.createConnection();
        } else if (this.companyId && this.ws?.readyState === WebSocket.OPEN) {
          // Send a ping to verify connection is alive
          console.log('📡 [WebSocket] Sending verification ping');
          this.send('ping', { timestamp: Date.now() });
        }
      }
    };
    
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityChangeListener);
    }
  }

  /**
   * Connect to WebSocket server
   */
  connect(companyId: number): void {
    // Prevent duplicate connections
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      console.log('🔌 [WebSocket] Already connecting...');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.companyId === companyId) {
      console.log('🔌 [WebSocket] Already connected to company', companyId);
      return;
    }

    // If switching companies, close existing connection first
    if (this.ws && this.companyId !== companyId) {
      console.log('🔌 [WebSocket] Switching companies, closing old connection');
      this.cleanup();
      // Reset event tracking for new company
      this.lastEventId = 0;
      this.processedEventIds.clear();
    }

    this.companyId = companyId;
    this.isManuallyDisconnected = false;
    
    // Load last event ID for this company
    this.loadLastEventId();
    
    this.createConnection();
  }

  /**
   * Create WebSocket connection
   */
  private createConnection(): void {
    if (!this.companyId) return;

    // Connect directly to backend WebSocket (bypass frontend proxy)
    // Auto-detect WebSocket URL from current origin if not configured
    // Detect placeholder strings from build and use fallback
    const getWsUrl = () => {
      const envWsUrl = process.env.REACT_APP_WS_URL;
      if (envWsUrl && !envWsUrl.startsWith('__')) return envWsUrl;

      const envApiUrl = process.env.REACT_APP_API_URL;
      if (envApiUrl && !envApiUrl.startsWith('__')) {
        const apiOrigin = envApiUrl.replace(/\/$/, '');
        return apiOrigin.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
      }

      if (typeof window === 'undefined') return 'wss://localhost:3333';

      const isLocalDev =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

      if (isLocalDev) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//localhost:3333`;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    };
    const backendHost = getWsUrl();
    const token = localStorage.getItem('token');
    const encodedToken = token ? encodeURIComponent(token) : '';
    const wsUrl = `${backendHost}/api/ws?companyId=${this.companyId}&token=${encodedToken}`;

    console.log('🔌 [WebSocket] Connecting to:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();

      // Add timeout to detect hanging connections
      setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.warn('⚠️ [WebSocket] Connection timeout after 10s, closing...');
          this.ws.close();
        }
      }, 10000);
    } catch (error) {
      console.error('❌ [WebSocket] Failed to create connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ [WebSocket] Connected successfully!');
      this.reconnectAttempts = 0;
      this.lastPong = Date.now();

      // Clear processed events cache on new connection to prevent blocking valid events
      this.processedEventIds.clear();
      console.log('🧹 [WebSocket] Cleared processed events cache');

      // Process queued messages
      this.flushMessageQueue();

      // Start heartbeat
      this.startHeartbeat();

      // Notify listeners
      this.emit('_connected', { companyId: this.companyId });
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        
        // Log ALL incoming messages for debugging
        if (message.event !== 'pong') {
          console.log('📩 [WebSocket] Raw message received:', message.event, message);
        }

        // Handle pong
        if (message.event === 'pong') {
          this.lastPong = Date.now();
          return;
        }

        // Handle connected event from server - trigger sync
        if (message.event === 'connected') {
          console.log('✅ [WebSocket] Server confirmed connection:', message.data);
          // Request sync for missed events
          this.requestSync();
          return;
        }

        // Handle sync response
        if (message.event === 'sync-response') {
          this.handleSyncResponse(message.data as SyncResponse);
          return;
        }

        // Process normal events with deduplication
        this.processIncomingEvent(message);
      } catch (error) {
        console.error('❌ [WebSocket] Failed to parse message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ [WebSocket] Error:', error);
    };

    this.ws.onclose = (event) => {
      console.log('🔌 [WebSocket] Disconnected. Code:', event.code, 'Reason:', event.reason);
      this.stopHeartbeat();
      this.isSyncing = false;

      if (!this.isManuallyDisconnected) {
        this.scheduleReconnect();
      }

      // Notify listeners
      this.emit('_disconnected', { code: event.code, reason: event.reason });
    };
  }

  /**
   * Process incoming event with deduplication
   */
  private processIncomingEvent(message: WebSocketMessage): void {
    // Extract event ID if present
    const eventId = message.data?.eventId as number || 0;
    const payload = message.data?.payload || message.data;

    // Check for duplicates
    if (eventId > 0 && this.isEventProcessed(eventId)) {
      console.log(`🔄 [WebSocket] Skipping duplicate event ${message.event} (id: ${eventId})`);
      return;
    }

    // Update tracking
    if (eventId > 0) {
      this.updateLastEventId(eventId);
      this.sendAck(eventId);
    }

    console.log('📨 [WebSocket] Event:', message.event, eventId > 0 ? `(id: ${eventId})` : '');
    this.emit(message.event, payload);
  }

  /**
   * Request sync for missed events
   */
  private requestSync(): void {
    if (this.isSyncing) {
      console.log('🔄 [WebSocket] Already syncing, skipping...');
      return;
    }

    if (this.lastEventId > 0) {
      console.log(`🔄 [WebSocket] Requesting sync from event ID ${this.lastEventId}`);
      this.isSyncing = true;
      this.send('sync', { lastEventId: this.lastEventId });
    } else {
      console.log('🔄 [WebSocket] No previous events, skipping sync');
    }
  }

  /**
   * Handle sync response from server
   */
  private handleSyncResponse(response: SyncResponse): void {
    console.log(`🔄 [WebSocket] Received ${response.events.length} sync events (hasMore: ${response.hasMore})`);

    // Process each sync event
    for (const event of response.events) {
      // Skip already processed events
      if (this.isEventProcessed(event.id)) {
        console.log(`🔄 [WebSocket] Skipping already processed sync event ${event.eventType} (id: ${event.id})`);
        continue;
      }

      // Update tracking
      this.updateLastEventId(event.id);

      // Emit the event
      console.log(`📨 [WebSocket] Sync event: ${event.eventType} (id: ${event.id})`);
      this.emit(event.eventType, event.payload);
    }

    // Update last event ID from response
    if (response.lastEventId > this.lastEventId) {
      this.lastEventId = response.lastEventId;
      this.saveLastEventId();
    }

    // Request more if available
    if (response.hasMore) {
      console.log('🔄 [WebSocket] More events available, requesting next batch...');
      this.send('sync', { lastEventId: this.lastEventId });
    } else {
      this.isSyncing = false;
      console.log('✅ [WebSocket] Sync complete');
      this.emit('_syncComplete', { lastEventId: this.lastEventId });
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    // Send ping every 25 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Check if we haven't received pong in 60 seconds (increased margin for stability)
        if (Date.now() - this.lastPong > 60000) {
          console.warn('⚠️ [WebSocket] No pong received, reconnecting...');
          this.ws.close();
          return;
        }

        this.send('ping', { timestamp: Date.now() });
      }
    }, 25000);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.isManuallyDisconnected) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ [WebSocket] Max reconnection attempts reached');
      this.emit('_maxReconnectAttempts', {});
      return;
    }

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1) + Math.random() * 1000,
      30000 // Max 30 seconds
    );

    console.log(`⏳ [WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.companyId && !this.isManuallyDisconnected) {
        this.createConnection();
      }
    }, delay);
  }

  /**
   * Process queued messages
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift();
      if (msg) {
        this.send(msg.event, msg.data);
      }
    }
  }

  /**
   * Cleanup connection resources
   */
  private cleanup(): void {
    this.stopHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on manual close
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Force reconnection
   */
  reconnect(): void {
    console.log('🔄 [WebSocket] Manual reconnect requested');
    if (this.companyId) {
      this.reconnectAttempts = 0;
      this.cleanup();
      this.isManuallyDisconnected = false;
      this.createConnection();
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    console.log('🔌 [WebSocket] Manual disconnect');
    this.isManuallyDisconnected = true;
    this.cleanup();
    this.listeners.clear();
    this.messageQueue = [];
    this.companyId = null;
    this.reconnectAttempts = 0;
    this.isSyncing = false;
    // Keep lastEventId and processedEventIds to handle reconnections
    
    // Remove visibility listener
    if (this.visibilityChangeListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
  }

  /**
   * Register an event listener
   */
  on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Remove an event listener
   */
  off(event: string, callback: EventCallback): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit(event: string, data: any): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ [WebSocket] Error in callback for event '${event}':`, error);
        }
      });
    }
  }

  /**
   * Send message to server
   */
  send(event: string, data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ event, data }));
      } catch (error) {
        console.error('❌ [WebSocket] Failed to send message:', error);
        this.messageQueue.push({ event, data });
      }
    } else {
      console.warn('⚠️ [WebSocket] Not connected, queuing message:', event);
      this.messageQueue.push({ event, data });
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state as string
   */
  getConnectionState(): string {
    if (!this.ws) return 'Disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'Connecting';
      case WebSocket.OPEN: return 'Connected';
      case WebSocket.CLOSING: return 'Closing';
      case WebSocket.CLOSED: return 'Closed';
      default: return 'Unknown';
    }
  }

  /**
   * Check if currently syncing
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Get the last processed event ID
   */
  getLastEventId(): number {
    return this.lastEventId;
  }

  /**
   * Manually trigger a sync request
   */
  triggerSync(): void {
    if (this.isConnected()) {
      this.isSyncing = false; // Reset flag to allow new sync
      this.requestSync();
    } else {
      console.warn('⚠️ [WebSocket] Cannot sync - not connected');
    }
  }

  /**
   * Clear sync state (useful for logout)
   */
  clearSyncState(): void {
    this.lastEventId = 0;
    this.processedEventIds.clear();
    try {
      localStorage.removeItem(LAST_EVENT_ID_KEY);
    } catch {
      // Ignore storage errors
    }
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
